import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Camera } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAuth, isManagerUp } from "../../lib/auth";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const CATS = ["Jewelry", "Watches", "Electronics", "Car", "Motorcycle", "Heavy Equipment", "Tools", "Other"];
const EMPTY = { name: "", category: "Jewelry", estimated_value: "", condition: "", storage_location: "", description: "", serial_no: "", brand: "", model: "", year: "", color: "", plate_no: "", chassis_no: "", fuel_percent: "", jewelry_type: "", gold_karat: "", weight_grams: "" };
const VEHICLE_CATS = ["Car", "Motorcycle", "Heavy Equipment"];
const JEWELRY_TYPES = ["Ring", "Necklace", "Bracelet", "Earrings", "Chain", "Pendant", "Other"];
const KARATS = ["24K", "22K", "21K", "18K", "14K", "10K"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const itemPhotoUrl = (item) => `${BACKEND}/api/shop/items/${item.id}/photo`;

export default function Items() {
  const { t } = useLang();
  const { user } = useAuth();
  const managerUp = isManagerUp(user);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/shop/items", { params: filter !== "all" ? { status: filter } : {} }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (i) => {
    setEditing(i);
    setForm({ name: i.name, category: i.category || "Other", estimated_value: i.estimated_value || "", condition: i.condition || "", storage_location: i.storage_location || "", description: i.description || "", serial_no: i.serial_no || "", brand: i.brand || "", model: i.model || "", year: i.year || "", color: i.color || "", plate_no: i.plate_no || "", chassis_no: i.chassis_no || "", fuel_percent: i.fuel_percent || "", jewelry_type: i.jewelry_type || "", gold_karat: i.gold_karat || "", weight_grams: i.weight_grams ?? "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, estimated_value: Number(form.estimated_value) || 0, weight_grams: form.weight_grams === "" || form.weight_grams == null ? null : Number(form.weight_grams) };
      if (editing) await API.patch(`/shop/items/${editing.id}`, payload);
      else await API.post("/shop/items", payload);
      toast.success(t("msg.saved"));
      setOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (i) => {
    if (!window.confirm(`${t("common.delete")} ${i.name}?`)) return;
    try {
      await API.delete(`/shop/items/${i.id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const uploadPhoto = async (item, file) => {
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    try {
      await API.post(`/shop/items/${item.id}/photo`, data);
      toast.success(t("msg.saved"));
      load();
      setEditing((e) => (e ? { ...e, photo_path: "updated" } : e));
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="items-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{t("items.title")}</h1>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="items-status-filter" className="w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">{t("common.status")}: All</SelectItem>
              {["pledged", "redeemed", "forfeited", "inventory", "auction", "sold"].map((s) => (
                <SelectItem key={s} value={s} data-testid={`items-filter-${s}`}>{t(`status.${s}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {managerUp && (
            <Button data-testid="new-item-button" onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Plus className="h-4 w-4 mr-1.5" />{t("items.new")}
            </Button>
          )}
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("items.photo")}</TableHead>
              <TableHead>{t("common.name")}</TableHead>
              <TableHead>{t("items.category")}</TableHead>
              <TableHead>{t("items.est_value")}</TableHead>
              <TableHead>{t("items.location")}</TableHead>
              <TableHead>{t("items.loan")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">{t("common.none")}</TableCell></TableRow>
            )}
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`item-row-${i.id.slice(0, 8)}`}>
                <TableCell>
                  {i.photo_path ? (
                    <img src={itemPhotoUrl(i)} alt={i.name} data-testid={`item-photo-${i.id.slice(0, 8)}`}
                      className="h-10 w-10 rounded-lg object-cover border border-slate-200" />
                  ) : (
                    <div className="h-10 w-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                      <Camera className="h-4 w-4 text-slate-400" />
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  {i.name}
                  {i.serial_no && <div className="font-mono text-xs text-slate-400 font-normal">{i.serial_no}</div>}
                  {i.jewelry_type && (
                    <div className="text-xs text-slate-400 font-normal" data-testid={`item-jewelry-${i.id.slice(0, 8)}`}>
                      {i.jewelry_type}{i.gold_karat ? ` · ${i.gold_karat}` : ""}{i.weight_grams ? ` · ${i.weight_grams}g` : ""}
                    </div>
                  )}
                </TableCell>
                <TableCell>{i.category}</TableCell>
                <TableCell className="font-mono">{money(i.estimated_value)}</TableCell>
                <TableCell>{i.storage_location || "—"}</TableCell>
                <TableCell className="font-mono text-sm">{i.loan_number || "—"}</TableCell>
                <TableCell><StatusBadge status={i.status} /></TableCell>
                <TableCell className="text-right">
                  {managerUp && (
                    <>
                      <button data-testid={`edit-item-${i.id.slice(0, 8)}`} onClick={() => openEdit(i)} className="p-2 hover:bg-slate-100 rounded-lg"><Pencil className="h-4 w-4 text-slate-600" /></button>
                      <button data-testid={`delete-item-${i.id.slice(0, 8)}`} onClick={() => remove(i)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="h-4 w-4 text-red-600" /></button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="item-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("common.edit") : t("items.new")}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>{t("common.name")}</Label>
              <Input data-testid="item-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("items.category")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="item-category-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("items.est_value")}</Label>
                <Input data-testid="item-value-input" type="number" min="0" step="0.01" value={form.estimated_value}
                  onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("items.condition")}</Label>
                <Input data-testid="item-condition-input" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>{t("items.serial")}</Label>
                <Input data-testid="item-serial-input" value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("items.location")}</Label>
                <Input data-testid="item-location-input" value={form.storage_location} onChange={(e) => setForm({ ...form, storage_location: e.target.value })} className="mt-1" />
              </div>
            </div>
            {VEHICLE_CATS.includes(form.category) && (
              <div className="grid grid-cols-3 gap-3 border border-slate-200 rounded-lg p-3" data-testid="vehicle-fields">
                <div>
                  <Label className="text-xs">{t("items.brand")}</Label>
                  <Input data-testid="item-brand-input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">{t("items.model")}</Label>
                  <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">{t("items.year")}</Label>
                  <Input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">{t("items.color")}</Label>
                  <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">{t("items.plate")}</Label>
                  <Input value={form.plate_no} onChange={(e) => setForm({ ...form, plate_no: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">{t("items.chassis")}</Label>
                  <Input value={form.chassis_no} onChange={(e) => setForm({ ...form, chassis_no: e.target.value })} className="mt-1 h-9" />
                </div>
              </div>
            )}
            {form.category === "Jewelry" && (
              <div className="grid grid-cols-3 gap-3 border border-slate-200 rounded-lg p-3" data-testid="jewelry-fields">
                <div>
                  <Label className="text-xs">{t("items.jewelry_type")}</Label>
                  <Select value={form.jewelry_type} onValueChange={(v) => setForm({ ...form, jewelry_type: v })}>
                    <SelectTrigger data-testid="item-jewelry-type-select" className="mt-1 h-9 bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {JEWELRY_TYPES.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("items.karat")}</Label>
                  <Select value={form.gold_karat} onValueChange={(v) => setForm({ ...form, gold_karat: v })}>
                    <SelectTrigger data-testid="item-karat-select" className="mt-1 h-9 bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {KARATS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("items.weight")}</Label>
                  <Input data-testid="item-weight-input" type="number" min="0" step="0.01" value={form.weight_grams}
                    onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} className="mt-1 h-9" />
                </div>
              </div>
            )}
            {editing && (
              <div>
                <Label>{t("items.photo")}</Label>
                <div className="mt-1 flex items-center gap-3">
                  {editing.photo_path && (
                    <img src={itemPhotoUrl(editing)} alt="" className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
                  )}
                  <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
                    <Camera className="h-4 w-4" />
                    {editing.photo_path ? t("items.change_photo") : t("items.upload_photo")}
                    <input type="file" accept="image/*" className="hidden" data-testid="item-photo-input"
                      onChange={(e) => uploadPhoto(editing, e.target.files[0])} />
                  </label>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
              <Button data-testid="item-save-button" type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">{t("common.save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
